import '../assets/NcActions-CiiQkX9v.css';
import Q from "../Components/NcButton.mjs";
import { N as X } from "./NcPopover--V3R3EKV.mjs";
import { G as Y } from "./GenRandomId-BW3iYFf9.mjs";
import { g as Z } from "./focusTrap-Be9GEB5C.mjs";
import { r as ee, o as te, a as ie } from "./_l10n-BaNxGGXy.mjs";
import ne, { computed as se } from "vue";
import { D as oe } from "./DotsHorizontal-DfVgEXgz.mjs";
import { n as re } from "./_plugin-vue2_normalizer-Bj5bLKV4.mjs";
ee(te);
const W = ".focusable", ce = {
  name: "NcActions",
  components: {
    NcButton: Q,
    DotsHorizontal: oe,
    NcPopover: X
  },
  provide() {
    return {
      /**
       * NcActions can be used as:
       * - Application menu (has menu role)
       * - Expanded block (has no specific role, should be used an element with expanded role)
       * - Popover with plain text or text inputs (has no specific role)
       * Depending on the usage (used items), the menu and its items should have different roles for a11y.
       * Provide the role for NcAction* components in the NcActions content.
       * @type {import('vue').ComputedRef<boolean>}
       */
      "NcActions:isSemanticMenu": se(() => this.actionsMenuSemanticType === "menu")
    };
  },
  props: {
    /**
     * Specify the open state of the popover menu
     */
    open: {
      type: Boolean,
      default: !1
    },
    /**
     * This disables the internal open management,
     * so the actions menu only respects the `open` prop.
     * This is e.g. necessary for the NcAvatar component
     * to only open the actions menu after loading it's entries has finished.
     */
    manualOpen: {
      type: Boolean,
      default: !1
    },
    /**
     * Force the actions to display in a three dot menu
     */
    forceMenu: {
      type: Boolean,
      default: !1
    },
    /**
     * Force the name to show for single actions
     */
    forceName: {
      type: Boolean,
      default: !1
    },
    /**
     * Specify the menu name
     */
    menuName: {
      type: String,
      default: null
    },
    /**
     * NcActions can be used as:
     *
     * - Application menu (has menu role)
     * - Navigation (has no specific role, should be used an element with expanded role)
     * - Popover with plain text or text inputs (has no specific role)
     *
     * By default the used type is automatically detected by components used in the default slot.#
     *
     * With Vue this is limited to direct children of the NcActions component.
     * So if you use a wrapper, you have to provide the semantic type yourself (see Example)
     *
     * Choose:
     *
     * - 'dialog' if you use any of these components: NcActionInput', 'NcActionTextEditable'
     * - 'menu' if you use any of these components: 'NcActionButton', 'NcActionButtonGroup', 'NcActionCheckbox', 'NcActionRadio'
     * - 'expanded' if using one of these: 'NcActionLink', 'NcActionRouter'. This represents an expanded block.
     * - 'tooltip' only to be used when a text without any interactive elements is used.
     * - Leave this property unset otherwise
     */
    forceSemanticType: {
      type: String,
      default: null,
      validator(e) {
        return ["dialog", "menu", "expanded", "tooltip"].includes(e);
      }
    },
    /**
     * Apply primary styling for this menu
     */
    primary: {
      type: Boolean,
      default: !1
    },
    /**
     * Specifies the button type used for trigger and single actions buttons
     * Accepted values: primary, secondary, tertiary, tertiary-no-background, tertiary-on-primary, error, warning, success. If left empty,
     * the default button style will be applied.
     */
    type: {
      type: String,
      validator(e) {
        return ["primary", "secondary", "tertiary", "tertiary-no-background", "tertiary-on-primary", "error", "warning", "success"].indexOf(e) !== -1;
      },
      default: null
    },
    /**
     * Icon to show for the toggle menu button
     * when more than one action is inside the actions component.
     * Only replace the default three-dot icon if really necessary.
     */
    defaultIcon: {
      type: String,
      default: ""
    },
    /**
     * Aria label for the actions menu.
     *
     * If `menuName` is defined this will not be used to prevent
     * any accessible name conflicts. This ensures that the
     * element can be activated via voice input.
     */
    ariaLabel: {
      type: String,
      default: ie("Actions")
    },
    /**
     * @deprecated To be removed in @nextcloud/vue 9. Migration guide: remove ariaHidden prop from NcAction* components.
     * @todo Add a check in @nextcloud/vue 9 that this prop is not provided,
     * otherwise root element will inherit incorrect aria-hidden.
     */
    ariaHidden: {
      type: Boolean,
      default: null
    },
    /**
     * Wanted direction of the menu
     */
    placement: {
      type: String,
      default: "bottom"
    },
    /**
     * DOM element for the actions' popover boundaries
     */
    boundariesElement: {
      type: Element,
      default: () => document.querySelector("body")
    },
    /**
     * Selector for the actions' popover container
     */
    container: {
      type: [String, Object, Element, Boolean],
      default: "body"
    },
    /**
     * Disabled state of the main button (single action or menu toggle)
     */
    disabled: {
      type: Boolean,
      default: !1
    },
    /**
     * Display x items inline out of the dropdown menu
     * Will be ignored if `forceMenu` is set
     */
    inline: {
      type: Number,
      default: 0
    }
  },
  emits: [
    "open",
    "update:open",
    "close",
    "focus",
    "blur",
    "click"
  ],
  setup() {
    const e = "menu-".concat(Y());
    return {
      randomId: e,
      triggerRandomId: "trigger-".concat(e)
    };
  },
  data() {
    return {
      opened: this.open,
      focusIndex: 0,
      /**
       * @type {'menu'|'expanded'|'dialog'|'tooltip'|'unknown'}
       */
      actionsMenuSemanticType: "unknown",
      externalFocusTrapStack: []
    };
  },
  computed: {
    triggerBtnType() {
      return this.type || (this.primary ? "primary" : this.menuName ? "secondary" : "tertiary");
    },
    /**
     * A11y roles and keyboard navigation configuration depending on the semantic type
     */
    config() {
      return {
        menu: {
          popupRole: "menu",
          withArrowNavigation: !0,
          withTabNavigation: !1,
          withFocusTrap: !1,
          triggerA11yAttr: {
            "aria-controls": this.opened ? this.randomId : null
          },
          popoverContainerA11yAttrs: {},
          popoverUlA11yAttrs: {
            id: this.randomId,
            role: "menu"
          }
        },
        expanded: {
          popupRole: void 0,
          withArrowNavigation: !1,
          withTabNavigation: !0,
          withFocusTrap: !1,
          triggerA11yAttr: {},
          popoverContainerA11yAttrs: {},
          popoverUlA11yAttrs: {}
        },
        dialog: {
          popupRole: "dialog",
          withArrowNavigation: !1,
          withTabNavigation: !0,
          withFocusTrap: !0,
          triggerA11yAttr: {
            "aria-controls": this.opened ? this.randomId : null
          },
          popoverContainerA11yAttrs: {
            id: this.randomId,
            role: "dialog",
            // Dialog must have a label
            "aria-labelledby": this.triggerRandomId,
            "aria-modal": "true"
          },
          popoverUlA11yAttrs: {}
        },
        tooltip: {
          popupRole: void 0,
          withArrowNavigation: !1,
          withTabNavigation: !1,
          withFocusTrap: !1,
          triggerA11yAttr: {},
          popoverContainerA11yAttrs: {},
          popoverUlA11yAttrs: {}
        },
        // Due to Vue limitations, we sometimes cannot determine the true type
        // As a fallback use both arrow navigation and focus trap
        unknown: {
          popupRole: void 0,
          role: void 0,
          withArrowNavigation: !0,
          withTabNavigation: !1,
          withFocusTrap: !0,
          triggerA11yAttr: {},
          popoverContainerA11yAttrs: {},
          popoverUlA11yAttrs: {}
        }
      }[this.actionsMenuSemanticType];
    }
  },
  watch: {
    // Watch parent prop
    open(e) {
      e !== this.opened && (this.opened = e);
    },
    opened() {
      this.intersectIntoCurrentFocusTrapStack();
    }
  },
  methods: {
    /**
     * Get the name of the action component
     *
     * @param {import('vue').VNode} action - a vnode with a NcAction* component instance
     * @return {string} the name of the action component
     */
    getActionName(e) {
      var i, n, s, o, r;
      return (r = (s = (n = (i = e == null ? void 0 : e.componentOptions) == null ? void 0 : i.Ctor) == null ? void 0 : n.extendOptions) == null ? void 0 : s.name) != null ? r : (o = e == null ? void 0 : e.componentOptions) == null ? void 0 : o.tag;
    },
    /**
     * When the component has its own focus trap, then it is managed by global trap stack by focus-trap.
     *
     * However if the component has no focus trap and is used inside another focus trap - there is an issue.
     * By default popover content is rendered in body or other container, which is likely outside the current focus trap containers.
     * It results in broken behavior from focus-trap.
     *
     * We need to pause all the focus traps for opening popover and then unpause them back after closing.
     */
    intersectIntoCurrentFocusTrapStack() {
      if (!this.config.withFocusTrap)
        if (this.opened) {
          this.externalFocusTrapStack = [...Z()];
          for (const e of this.externalFocusTrapStack)
            e.pause();
        } else {
          for (const e of this.externalFocusTrapStack)
            e.unpause();
          this.externalFocusTrapStack = [];
        }
    },
    /**
     * Do we have exactly one Action and
     * is it allowed as a standalone element?
     *
     * @param {import('vue').VNode} action The action to check
     * @return {boolean}
     */
    isValidSingleAction(e) {
      return ["NcActionButton", "NcActionLink", "NcActionRouter"].includes(this.getActionName(e));
    },
    /**
     * Check whether a icon prop value is an URL or not
     * @param {string} url The icon prop value
     */
    isIconUrl(e) {
      try {
        return !!new URL(e, e.startsWith("/") ? window.location.origin : void 0);
      } catch {
        return !1;
      }
    },
    // MENU STATE MANAGEMENT
    openMenu(e) {
      this.opened || (this.opened = !0, this.$emit("update:open", !0), this.$emit("open"));
    },
    async closeMenu(e = !0) {
      var i, n;
      this.opened && (await this.$nextTick(), this.opened = !1, (i = this.$refs.popover) == null || i.clearFocusTrap({ returnFocus: e }), this.$emit("update:open", !1), this.$emit("close"), this.focusIndex = 0, e && ((n = this.$refs.menuButton) == null || n.$el.focus()));
    },
    /**
     * Called when popover is shown after the show delay
     */
    onOpen() {
      this.$nextTick(() => {
        this.focusFirstAction(null), this.resizePopover();
      });
    },
    /**
     * Hanle resizing the popover to make sure users can discover there is more to scroll
     */
    resizePopover() {
      const e = this.$refs.menu.closest(".v-popper__inner"), i = Number.parseFloat(window.getComputedStyle(e).maxHeight);
      if (this.$refs.menu.clientHeight > i) {
        let s = 0, o = 0;
        for (const r of this.$refs.menuList.children) {
          if (s + r.clientHeight / 2 > i) {
            e.style.height = "".concat(s - o / 2, "px");
            break;
          }
          o = r.clientHeight, s += o;
        }
      }
    },
    // MENU KEYS & FOCUS MANAGEMENT
    /**
     * @return {HTMLElement|null}
     */
    getCurrentActiveMenuItemElement() {
      return this.$refs.menu.querySelector("li.active");
    },
    /**
     * @return {NodeListOf<HTMLElement>}
     */
    getFocusableMenuItemElements() {
      return this.$refs.menu.querySelectorAll(W);
    },
    /**
     * Focus nearest focusable item on mouse move.
     * DO NOT change the focus if the target is already focused
     * this will prevent issues with input being unfocused
     * on mouse move
     * @param {PointerEvent} event - The mouse move event
     */
    onMouseFocusAction(e) {
      if (document.activeElement === e.target)
        return;
      const i = e.target.closest("li");
      if (i && this.$refs.menu.contains(i)) {
        const n = i.querySelector(W);
        if (n) {
          const o = [...this.getFocusableMenuItemElements()].indexOf(n);
          o > -1 && (this.focusIndex = o, this.focusAction());
        }
      }
    },
    /**
     * Dispatches the keydown listener to different handlers
     *
     * @param {object} event The keydown event
     */
    onKeydown(e) {
      if (e.key === "Tab") {
        if (this.config.withFocusTrap)
          return;
        if (!this.config.withTabNavigation) {
          this.closeMenu(!0);
          return;
        }
        e.preventDefault();
        const i = this.getFocusableMenuItemElements(), n = [...i].indexOf(document.activeElement);
        if (n === -1)
          return;
        const s = e.shiftKey ? n - 1 : n + 1;
        (s < 0 || s === i.length) && this.closeMenu(!0), this.focusIndex = s, this.focusAction();
        return;
      }
      this.config.withArrowNavigation && (e.key === "ArrowUp" && this.focusPreviousAction(e), e.key === "ArrowDown" && this.focusNextAction(e), e.key === "PageUp" && this.focusFirstAction(e), e.key === "PageDown" && this.focusLastAction(e)), e.key === "Escape" && (this.closeMenu(), e.preventDefault());
    },
    onTriggerKeydown(e) {
      e.key === "Escape" && this.actionsMenuSemanticType === "tooltip" && this.closeMenu();
    },
    removeCurrentActive() {
      const e = this.$refs.menu.querySelector("li.active");
      e && e.classList.remove("active");
    },
    focusAction() {
      const e = this.getFocusableMenuItemElements()[this.focusIndex];
      if (e) {
        this.removeCurrentActive();
        const i = e.closest("li.action");
        e.focus(), i && i.classList.add("active");
      }
    },
    focusPreviousAction(e) {
      this.opened && (this.focusIndex === 0 ? this.focusLastAction(e) : (this.preventIfEvent(e), this.focusIndex = this.focusIndex - 1), this.focusAction());
    },
    focusNextAction(e) {
      if (this.opened) {
        const i = this.getFocusableMenuItemElements().length - 1;
        this.focusIndex === i ? this.focusFirstAction(e) : (this.preventIfEvent(e), this.focusIndex = this.focusIndex + 1), this.focusAction();
      }
    },
    focusFirstAction(e) {
      if (this.opened) {
        this.preventIfEvent(e);
        const i = [...this.getFocusableMenuItemElements()].findIndex((n) => n.getAttribute("aria-checked") === "true" && n.getAttribute("role") === "menuitemradio");
        this.focusIndex = i > -1 ? i : 0, this.focusAction();
      }
    },
    focusLastAction(e) {
      this.opened && (this.preventIfEvent(e), this.focusIndex = this.getFocusableMenuItemElements().length - 1, this.focusAction());
    },
    preventIfEvent(e) {
      e && (e.preventDefault(), e.stopPropagation());
    },
    onFocus(e) {
      this.$emit("focus", e);
    },
    onBlur(e) {
      this.$emit("blur", e), this.actionsMenuSemanticType === "tooltip" && this.$refs.menu && this.getFocusableMenuItemElements().length === 0 && this.closeMenu(!1);
    },
    onClick(e) {
      this.$emit("click", e);
    }
  },
  /**
   * The render function to display the component
   *
   * @param {Function} h The function to create VNodes
   * @return {object|undefined} The created VNode
   */
  render(e) {
    const i = (this.$slots.default || []).filter((t) => this.getActionName(t));
    if (i.length === 0)
      return;
    let n = i.filter(this.isValidSingleAction);
    this.forceMenu && n.length > 0 && this.inline > 0 && (ne.util.warn("Specifying forceMenu will ignore any inline actions rendering."), n = []);
    const s = n.slice(0, this.inline), o = i.filter((t) => !s.includes(t));
    if (this.forceSemanticType)
      this.actionsMenuSemanticType = this.forceSemanticType;
    else {
      const t = ["NcActionInput", "NcActionTextEditable"], a = ["NcActionButton", "NcActionButtonGroup", "NcActionCheckbox", "NcActionRadio"], l = ["NcActionLink", "NcActionRouter"], u = o.some((c) => t.includes(this.getActionName(c))), p = o.some((c) => a.includes(this.getActionName(c))), h = o.some((c) => l.includes(this.getActionName(c)));
      u ? this.actionsMenuSemanticType = "dialog" : p ? this.actionsMenuSemanticType = "menu" : h ? this.actionsMenuSemanticType = "expanded" : i.filter((d) => this.getActionName(d).startsWith("NcAction")).length === i.length ? this.actionsMenuSemanticType = "tooltip" : this.actionsMenuSemanticType = "unknown";
    }
    const r = (t) => {
      var y, v, b, I, w, T, N, x, S, k, F, M, m, $, E, B, C, L, O, _, R, D, P, U, H, z, V, q, K, G;
      const a = (v = (y = t == null ? void 0 : t.componentOptions) == null ? void 0 : y.propsData) == null ? void 0 : v.icon, l = (T = (w = (I = (b = t == null ? void 0 : t.data) == null ? void 0 : b.scopedSlots) == null ? void 0 : I.icon()) == null ? void 0 : w[0]) != null ? T : this.isIconUrl(a) ? e("img", { class: "action-item__menutoggle__icon", attrs: { src: a, alt: "" } }) : e("span", { class: ["icon", a] }), u = ((N = t == null ? void 0 : t.data) == null ? void 0 : N.attrs) || {}, p = (S = (x = t == null ? void 0 : t.componentOptions) == null ? void 0 : x.listeners) == null ? void 0 : S.click, h = ($ = (m = (M = (F = (k = t == null ? void 0 : t.componentOptions) == null ? void 0 : k.children) == null ? void 0 : F[0]) == null ? void 0 : M.text) == null ? void 0 : m.trim) == null ? void 0 : $.call(m), c = ((B = (E = t == null ? void 0 : t.componentOptions) == null ? void 0 : E.propsData) == null ? void 0 : B.ariaLabel) || h, d = this.forceName ? h : "";
      let g = (L = (C = t == null ? void 0 : t.componentOptions) == null ? void 0 : C.propsData) == null ? void 0 : L.title;
      this.forceName || g || (g = h);
      const f = { ...(_ = (O = t == null ? void 0 : t.componentOptions) == null ? void 0 : O.propsData) != null ? _ : {} }, j = ["submit", "reset"].includes(f.type) ? f.modelValue : "button";
      return delete f.modelValue, delete f.type, e(
        "NcButton",
        {
          class: [
            "action-item action-item--single",
            (R = t == null ? void 0 : t.data) == null ? void 0 : R.staticClass,
            (D = t == null ? void 0 : t.data) == null ? void 0 : D.class
          ],
          attrs: {
            ...u,
            "aria-label": c,
            title: g
          },
          ref: (P = t == null ? void 0 : t.data) == null ? void 0 : P.ref,
          props: {
            // If it has a menuName, we use a secondary button
            type: this.type || (d ? "secondary" : "tertiary"),
            disabled: this.disabled || ((H = (U = t == null ? void 0 : t.componentOptions) == null ? void 0 : U.propsData) == null ? void 0 : H.disabled),
            pressed: (V = (z = t == null ? void 0 : t.componentOptions) == null ? void 0 : z.propsData) == null ? void 0 : V.modelValue,
            nativeType: j,
            ...f
          },
          on: {
            focus: this.onFocus,
            blur: this.onBlur,
            // forward any pressed state from NcButton just like NcActionButton does
            "update:pressed": (G = (K = (q = t == null ? void 0 : t.componentOptions) == null ? void 0 : q.listeners) == null ? void 0 : K["update:modelValue"]) != null ? G : () => {
            },
            // If we have a click listener,
            // we bind it to execute on click and forward the click event
            ...!!p && {
              click: (J) => {
                p && p(J);
              }
            }
          }
        },
        [
          e("template", { slot: "icon" }, [l]),
          d
        ]
      );
    }, A = (t) => {
      var l, u;
      const a = ((l = this.$slots.icon) == null ? void 0 : l[0]) || (this.defaultIcon ? e("span", { class: ["icon", this.defaultIcon] }) : e("DotsHorizontal", {
        props: {
          size: 20
        }
      }));
      return e(
        "NcPopover",
        {
          ref: "popover",
          props: {
            delay: 0,
            handleResize: !0,
            shown: this.opened,
            placement: this.placement,
            boundary: this.boundariesElement,
            container: this.container,
            popoverBaseClass: "action-item__popper",
            popupRole: this.config.popupRole,
            setReturnFocus: this.config.withFocusTrap ? (u = this.$refs.menuButton) == null ? void 0 : u.$el : null,
            focusTrap: this.config.withFocusTrap
          },
          // For some reason the popover component
          // does not react to props given under the 'props' key,
          // so we use both 'attrs' and 'props'
          attrs: {
            delay: 0,
            handleResize: !0,
            shown: this.opened,
            placement: this.placement,
            boundary: this.boundariesElement,
            container: this.container,
            ...this.manualOpen && { triggers: [] }
          },
          on: {
            show: this.openMenu,
            "apply-show": this.onOpen,
            hide: this.closeMenu
          }
        },
        [
          e("NcButton", {
            class: "action-item__menutoggle",
            props: {
              type: this.triggerBtnType,
              disabled: this.disabled
            },
            slot: "trigger",
            ref: "menuButton",
            attrs: {
              id: this.triggerRandomId,
              "aria-label": this.menuName ? null : this.ariaLabel,
              ...this.config.triggerA11yAttr
            },
            on: {
              focus: this.onFocus,
              blur: this.onBlur,
              click: this.onClick,
              keydown: this.onTriggerKeydown
            }
          }, [
            e("template", { slot: "icon" }, [a]),
            this.menuName
          ]),
          e("div", {
            class: {
              open: this.opened
            },
            attrs: {
              tabindex: "-1",
              ...this.config.popoverContainerA11yAttrs
            },
            on: {
              keydown: this.onKeydown,
              mousemove: this.onMouseFocusAction
            },
            ref: "menu"
          }, [
            e("ul", {
              attrs: {
                tabindex: "-1",
                ...this.config.popoverUlA11yAttrs
              },
              ref: "menuList"
            }, [
              t
            ])
          ])
        ]
      );
    };
    return i.length === 1 && n.length === 1 && !this.forceMenu ? r(i[0]) : (this.$nextTick(() => {
      this.opened && this.$refs.menu && (this.resizePopover(), (this.$refs.menu.querySelector("li.active") || []).length === 0 && this.focusFirstAction());
    }), s.length > 0 && this.inline > 0 ? e(
      "div",
      {
        class: [
          "action-items",
          "action-item--".concat(this.triggerBtnType)
        ]
      },
      [
        // Render inline actions
        ...s.map(r),
        // render the rest within the popover menu
        o.length > 0 ? e(
          "div",
          {
            class: [
              "action-item",
              {
                "action-item--open": this.opened
              }
            ]
          },
          [
            A(o)
          ]
        ) : null
      ]
    ) : e(
      "div",
      {
        class: [
          "action-item action-item--default-popover",
          "action-item--".concat(this.triggerBtnType),
          {
            "action-item--open": this.opened
          }
        ]
      },
      [
        A(i)
      ]
    ));
  }
}, ae = null, le = null;
var ue = /* @__PURE__ */ re(
  ce,
  ae,
  le,
  !1,
  null,
  "7f6b7570",
  null,
  null
);
const ve = ue.exports;
export {
  ve as N
};
